import { Link, useLocation, useSearchParams } from '@remix-run/react'
import { GlobeIcon, GlobeLockIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CartDrawerTrigger } from '~/components/cart'
import { ReorderButton } from '~/components/ShopReorder'
import { makeShopOwnPath } from '~/helpers/routes'
import { useOptionalSeller } from '~/hooks/data/use-seller'
import { useShop } from '~/hooks/data/use-shop'
import { Badge, Button, Dropdown, IconButton, Objects, PlusSquares } from '~/ui'
import { cn } from '~/utils/classnames'

interface Navigation {
  counts: {
    products: {
      published: number
      unpublished: number
    }
    collections: {
      published: number
      unpublished: number
    }
  }
}

export const ShopNavbar = ({ counts }: Navigation) => {
  const shop = useShop()
  const seller = useOptionalSeller()
  const location = useLocation()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const status = (searchParams.get('filter') || 'published') as
    | 'published'
    | 'unpublished'

  const activeTab = useMemo(() => {
    if (location.pathname.includes('/collections')) {
      return 'collections'
    }
    return 'products'
  }, [location.pathname])
  const [hover, setHover] = useState<typeof activeTab | null>(null)

  // Show reorder button only when seller is logged in and there are more than 1 items
  const showReorderButton = seller && counts[activeTab][status] > 1

  const tabs = useMemo(() => {
    if (!seller && counts.collections.published === 0)
      return ['products'] as const

    return ['products', 'collections'] as const
  }, [seller, counts])

  return (
    <header
      className={cn(
        'bg-background sticky top-0 z-20 flex w-full flex-wrap gap-2 py-2 @sm:py-4',
        // since padding is applied in the parent, we need to offset the background to cover content that might fall outside the padding
        'before:bg-background *:relative before:absolute before:-inset-x-2 before:inset-y-0'
      )}
    >
      <ul className="mr-auto flex gap-2">
        {tabs.map((tab) => (
          <li key={tab}>
            <Button
              variant="ghost"
              data-active={activeTab === tab}
              onMouseEnter={() => setHover(tab)}
              onMouseLeave={() => setHover(null)}
              asChild
            >
              <Link
                to={{
                  pathname: makeShopOwnPath(
                    shop,
                    tab === 'products' ? '/' : '/collections'
                  ),
                  search: location.search,
                }}
                preventScrollReset
                replace
                state={{ preventScrollReset: true }}
              >
                {seller && tab === 'products' && (
                  <PlusSquares
                    className="hidden text-base @sm:block"
                    isHovered={hover === 'products'}
                  />
                )}
                {seller && tab === 'collections' && (
                  <Objects
                    className="hidden text-base @sm:block"
                    isHovered={hover === 'collections'}
                  />
                )}
                {t(`page.products.top_navigation.${tab}`)}
                {!seller && (
                  <Badge className="text-2xs">{counts[tab].published}</Badge>
                )}
              </Link>
            </Button>
          </li>
        ))}
      </ul>

      {showReorderButton && <ReorderButton activeTab={activeTab} />}
      {seller && <ModeSwitcher activeTab={activeTab} />}
      {!seller && <CartDrawerTrigger />}
    </header>
  )
}

interface ModeSwitcherProps {
  className?: string
  activeTab: 'products' | 'collections'
}

const ModeSwitcher = ({ className, activeTab }: ModeSwitcherProps) => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const status = searchParams.get('filter') || 'published'

  const handleChangeStatus = (value: string) => {
    setSearchParams((prev) => {
      if (value === 'published' && prev.has('filter')) {
        prev.delete('filter')
      }

      if (value === 'unpublished') {
        prev.set('filter', 'unpublished')
      }

      return prev
    })
  }

  const isPublished = status === 'published'

  return (
    <Dropdown placement="bottom-end">
      <Dropdown.Trigger asChild>
        <IconButton
          aria-label={t('noun.published')}
          variant="ghost"
          className={className}
        >
          {isPublished ? <GlobeIcon /> : <GlobeLockIcon />}
        </IconButton>
      </Dropdown.Trigger>
      <Dropdown.Items className="min-w-72">
        <Dropdown.Item
          className="flex items-center gap-3 py-2"
          onSelect={() => handleChangeStatus('published')}
        >
          <div className="bg-foreground/6 flex size-8 shrink-0 items-center justify-center rounded-md">
            <GlobeIcon className="size-4" />
          </div>
          <div className="text-left text-sm">
            <div className="font-medium">{t('noun.published')}</div>
            <div className="text-foreground/48">
              {t('components.mode_switcher.published_item_description', {
                item: t(
                  `page.products.top_navigation.${activeTab}`
                ).toLowerCase(),
              })}
            </div>
          </div>
        </Dropdown.Item>
        <Dropdown.Item
          className="flex items-center gap-3 py-2"
          onSelect={() => handleChangeStatus('unpublished')}
        >
          <div className="bg-foreground/6 flex size-8 shrink-0 items-center justify-center rounded-md">
            <GlobeLockIcon className="size-4" />
          </div>
          <div className="text-left text-sm">
            <div className="font-medium">{t('noun.unpublished')}</div>
            <div className="text-foreground/48">
              {t('components.mode_switcher.unpublished_item_description', {
                item: t(
                  `page.products.top_navigation.${activeTab}`
                ).toLowerCase(),
              })}
            </div>
          </div>
        </Dropdown.Item>
      </Dropdown.Items>
    </Dropdown>
  )
}
